import { humanizeEligibility, resolveRoleEscalationEligibility } from "@swivl-mono/helpers";
import {
  APIScope,
  APIScopePermission,
  DEFAULT_ROLE_SLUGS,
  DefaultRoleSlug,
  FeatureFlagKey,
  RoleEscalationEligibility,
  RoleInterface,
  isDefaultRoleSlug
} from "@swivl-mono/types";
import { Button, Input, LabelPosition, LayoutHeader, Panel, Switch, TextArea, Toggle } from "@swivl-mono/ui";
import { useNavigate, useParams } from "react-router-dom";

import { ShowIf } from "../../../Components/ShowIf/ShowIf";
import { isFeatureEnabled } from "../../../Helpers/FeatureFlags/isFeatureEnabled";
import { useHasPermission } from "../../../Helpers/Permissions/useHasPermission";
import { Member } from "../../../Models/Member/Member.Current";
import RolesModel from "../../../Models/Roles/Roles.Model";
import { PermissionsGroupSection } from "./Components/PermissionsGroupSection";

export interface ActionAndName {
  action: APIScope;
  label: string;
}

interface Props {
  role: RoleInterface;
}

export function RoleSettingsDetailsView(props: Props) {
  const { role } = props;
  const { roles, defaultRoles, parentRoles } = RolesModel.useState();
  const navigate = useNavigate();
  const canWriteRoles = useHasPermission(APIScope.ROLES_SET);
  const v3EscalationsEnabled = isFeatureEnabled(FeatureFlagKey.Snowy);
  // const params = useParams();
  // const roleId = params.roleId;
  // if (!role) return <div>Loading...</div>;

  // let role:RoleInterface | undefined = undefined;
  // if (roleId) {
  //   for (let key in roles) {
  //     if (roles[key].id === parseInt(roleId)) {
  //       role = roles[key];
  //     }
  //   }
  //   if (!role) {
  //     for (let key in parentRoles) {
  //       if (parentRoles[key].id === parseInt(roleId)) {
  //         role = parentRoles[key];
  //       }
  //     }
  //   }
  // }

  /*  "fms-edit": "allowed",
    "fms-view": "allowed",
    "apps-edit": "allowed",
    "logged-in": "allowed",
    "roles-get": "allowed",
    "roles-set": "allowed",
    "local-only": "allowed",
    "roles-view": "allowed",
    "members-get": "allowed",
    "members-set": "allowed",
    "reviews-view": "allowed",
    "location-view": "allowed",
    "user-crm-view": "allowed",
    "user-crm-write": "allowed",
    "sequences-edit": "allowed",
    "sequences-view": "allowed",
    "user-crm-write": "allowed",
    "workflows-edit": "allowed",
    "workflows-view": "allowed",
    "location-create": "allowed",
    "escalations-edit": "allowed",
    "super-admin-only": "allowed",
    "conversation-view": "allowed",
    "escalations-handle": "allowed",
    "locations-all-view": "allowed",
    "conversation-takeover": "allowed",
    "location-details-read": "allowed",
    "location-details-write": "allowed"
    */

  if (!role) return <div>Role Not Found</div>;

  const isDefault = isDefaultRoleSlug(role.slug);
  const readOnly = isDefault || !canWriteRoles || role.orgId !== Member.orgId;

  // Build a slug→role map for resolving inheritance display.
  const rolesBySlug = { ...(defaultRoles ?? {}), ...(parentRoles ?? {}), ...(roles ?? {}) };
  const parentRole = role.parentSlug ? rolesBySlug[role.parentSlug] : undefined;
  const resolvedParentEligibility = parentRole ? resolveRoleEscalationEligibility(parentRole, rolesBySlug) : "all";

  return (
    <div className="flex flex-col px-6 pb-6">
      <LayoutHeader noPadding={true}>
        <h2>Role Details </h2>
        {isDefault ? (
          <p className="text-sm text-grey-500">System default — cannot be modified or deleted.</p>
        ) : (
          <ShowIf scope={APIScope.ROLES_SET}>
            <Button
              onClick={async () => {
                if (window.confirm("Are you sure you want to delete this role?")) {
                  await RolesModel.deleteRole(role.id);
                  navigate("/settings/roles");
                }
              }}
            >
              Delete Role
            </Button>
          </ShowIf>
        )}
      </LayoutHeader>
      <div className="flex flex-col gap-2">
        {isDefault ? (
          <>
            <div>
              <label className="mb-1 block text-sm font-semibold text-grey-700">Display Name</label>
              <p className="rounded border border-grey-200 bg-grey-50 px-3 py-2 text-sm">{role.name}</p>
            </div>
            <div>
              <label className="mb-1 block text-sm font-semibold text-grey-700">Description</label>
              <p className="rounded border border-grey-200 bg-grey-50 px-3 py-2 text-sm">{role.description}</p>
            </div>
            {v3EscalationsEnabled && (
              <div className="mb-4">
                <label className="mb-1 block text-sm font-semibold text-grey-700">Default escalation eligibility</label>
                <p className="rounded border border-grey-200 bg-grey-50 px-3 py-2 text-sm">
                  {humanizeEligibility(role.escalationEligibility ?? "all")}
                </p>
                <p className="mt-1 text-xs text-grey-500">
                  System default. Cannot be changed. Create a child role with this one as its parent to override.
                </p>
              </div>
            )}
          </>
        ) : (
          <>
            <Input
              value={role.name}
              labelPosition={LabelPosition.TOP}
              label="Display Name"
              readOnly={readOnly}
              onChange={(e) => {
                let updatedRole = { ...role };
                updatedRole.name = e.target.value;
                RolesModel.updateRole(updatedRole);
              }}
            />
            <Input
              value={role.description}
              labelPosition={LabelPosition.TOP}
              label="Description"
              containerClassName="mb-4"
              readOnly={readOnly}
              onChange={(e) => {
                let updatedRole = { ...role };
                updatedRole.description = e.target.value;
                RolesModel.updateRole(updatedRole);
              }}
            />

            <div className="mb-4">
              <label className="mb-1 block text-sm font-semibold text-grey-700">Parent role</label>
              <select
                className="w-full rounded border border-grey-300 px-3 py-2 text-sm disabled:opacity-50"
                disabled={readOnly}
                value={role.parentSlug ?? ""}
                onChange={(e) => {
                  const updatedRole = { ...role, parentSlug: e.target.value as DefaultRoleSlug };
                  RolesModel.updateRole(updatedRole);
                }}
              >
                {!role.parentSlug && <option value="">(Pick a parent role)</option>}
                {DEFAULT_ROLE_SLUGS.map((slug) => (
                  <option key={slug} value={slug}>
                    {rolesBySlug[slug]?.name ?? slug}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-grey-500">
                Every custom role inherits from one of the system defaults. Inheritance covers escalation eligibility and any permissions this role
                doesn&apos;t explicitly set.
              </p>
            </div>

            {v3EscalationsEnabled && (
              <div className="mb-4">
                <label className="mb-1 block text-sm font-semibold text-grey-700">Default escalation eligibility</label>
                <select
                  className="w-full rounded border border-grey-300 px-3 py-2 text-sm disabled:opacity-50"
                  disabled={readOnly}
                  value={role.escalationEligibility ?? ""}
                  onChange={(e) => {
                    const updatedRole = {
                      ...role,
                      escalationEligibility: e.target.value === "" ? null : (e.target.value as RoleEscalationEligibility)
                    };
                    RolesModel.updateRole(updatedRole);
                  }}
                >
                  <option value="">
                    {parentRole
                      ? `(Inherit from ${parentRole.name}: ${humanizeEligibility(resolvedParentEligibility)})`
                      : "(Inherit from parent role)"}
                  </option>
                  <option value="all">All — receive escalations and tickets</option>
                  <option value="tickets-only">Tickets only — no live escalations</option>
                  <option value="none">None — excluded from both</option>
                </select>
                <p className="mt-1 text-xs text-grey-500">
                  Default for members holding this role. Individual members can override on their detail page.
                </p>
              </div>
            )}
          </>
        )}

        <PermissionsGroupSection
          heading="CRM"
          role={role}
          readOnly={readOnly}
          actions={[
            { label: "View CRM Records", action: APIScope.USER_CRM_VIEW },
            { label: "Edit CRM Records", action: APIScope.USER_CRM_WRITE }
          ]}
        />

        <PermissionsGroupSection
          heading="Locations"
          role={role}
          readOnly={readOnly}
          actions={[
            { label: "View Location List", action: APIScope.LOCATIONS_ALL_VIEW },
            { label: "Create Locations", action: APIScope.LOCATION_CREATE },
            { label: "View Location Details", action: APIScope.LOCATION_DETAILS_READ },
            { label: "Edit Location Details", action: APIScope.LOCATION_DETAILS_WRITE }
          ]}
        />

        <PermissionsGroupSection
          heading="Members"
          role={role}
          readOnly={readOnly}
          actions={[
            { label: "View Members", action: APIScope.MEMBERS_GET },
            { label: "Manage Members", action: APIScope.MEMBERS_SET }
          ]}
        />

        <PermissionsGroupSection
          heading="Roles"
          role={role}
          readOnly={readOnly}
          actions={[
            { label: "View Roles", action: APIScope.ROLES_VIEW },
            { label: "Write Roles", action: APIScope.ROLES_SET }
          ]}
        />

        <PermissionsGroupSection
          heading="Conversations"
          role={role}
          readOnly={readOnly}
          actions={[
            { label: "View Conversations", action: APIScope.CONVERSATION_VIEW },
            { label: "Takeover Conversations", action: APIScope.CONVERSATION_TAKEOVER }
          ]}
        />

        <PermissionsGroupSection
          heading="Tickets"
          role={role}
          readOnly={readOnly}
          actions={[
            { label: "View Tickets", action: APIScope.TICKETS_VIEW },
            { label: "Edit Tickets", action: APIScope.TICKETS_EDIT }
          ]}
        />

        <PermissionsGroupSection
          heading="Widgets & Brands"
          role={role}
          readOnly={readOnly}
          actions={[
            { label: "View Widgets & Brands, Edit CTAs", action: APIScope.WIDGETS_AND_BRANDS_EDITOR },
            { label: "Manage Widgets & Brands", action: APIScope.WIDGETS_AND_BRANDS_ADMIN }
          ]}
        />

        <PermissionsGroupSection
          heading="Escalations"
          role={role}
          readOnly={readOnly}
          actions={[
            { label: "Handle Escalations", action: APIScope.ESCALATIONS_HANDLE },
            { label: "Edit Escalations", action: APIScope.ESCALATIONS_EDIT }
          ]}
        />

        <PermissionsGroupSection
          heading="Workflows"
          role={role}
          readOnly={readOnly}
          actions={[
            { label: "View Workflows", action: APIScope.WORKFLOWS_VIEW },
            { label: "Edit Workflows", action: APIScope.WORKFLOWS_EDIT }
          ]}
        />

        <PermissionsGroupSection
          heading="Sequences"
          role={role}
          readOnly={readOnly}
          actions={[
            { label: "View Sequences", action: APIScope.SEQUENCES_VIEW },
            { label: "Edit Sequences", action: APIScope.SEQUENCES_EDIT }
          ]}
        />

        <PermissionsGroupSection
          heading="FMS Integrations"
          role={role}
          readOnly={readOnly}
          actions={[
            { label: "View FMS Integrations", action: APIScope.FMS_VIEW },
            { label: "Edit FMS Integrations", action: APIScope.FMS_EDIT }
          ]}
        />

        <PermissionsGroupSection
          heading="Reviews"
          role={role}
          readOnly={readOnly}
          actions={[{ label: "Manage Reviews", action: APIScope.REVIEWS_EDIT }]}
        />

        <PermissionsGroupSection heading="Apps" role={role} readOnly={readOnly} actions={[{ label: "Edit Apps", action: APIScope.APPS_EDIT }]} />
      </div>
    </div>
  );
}
